import { useEffect, useState } from 'react'
import type { Preset, PresetFireResult, Source, Destination } from '@ultrix/shared'
import { Sheet } from './Sheet'
import { firePreset } from '../api'

interface Props {
  preset: Preset
  onClose: () => void
  sourcesByNumber: Map<number, Source>
  destinationsByNumber: Map<number, Destination>
}

type Status = 'idle' | 'firing' | 'done' | 'error'

export function PresetFireSheet({ preset, onClose, sourcesByNumber, destinationsByNumber }: Props) {
  const [status, setStatus] = useState<Status>('idle')
  const [result, setResult] = useState<PresetFireResult | null>(null)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  useEffect(() => {
    if (status !== 'done') return
    const t = setTimeout(onClose, 3_000)
    return () => clearTimeout(t)
  }, [status, onClose])

  async function onFire() {
    setStatus('firing')
    setErrorMsg(null)
    try {
      const r = await firePreset(preset.id)
      setResult(r)
      setStatus('done')
    } catch (e) {
      setErrorMsg(e instanceof Error ? e.message : String(e))
      setStatus('error')
    }
  }

  function sourceName(n: number): string {
    return sourcesByNumber.get(n)?.name ?? `#${n}`
  }
  function destName(n: number): string {
    return destinationsByNumber.get(n)?.name ?? `#${n}`
  }

  const total = preset.takes.length
  const successCount = result ? result.results.filter((r) => r.ok).length : 0
  const failed = result ? result.results.filter((r) => !r.ok) : []

  return (
    <Sheet open onClose={status === 'firing' ? () => {} : onClose}>
      <div className="flex flex-col gap-3">
        <div>
          <h2 className="text-lg font-semibold text-ink">Fire ‘{preset.name}’?</h2>
          <div className="text-xs text-sub">{total} route{total === 1 ? '' : 's'}</div>
        </div>

        <div className="border border-border rounded-md bg-panel max-h-64 overflow-y-auto divide-y divide-border">
          {preset.takes.length === 0 ? (
            <div className="p-3 text-sub text-sm">No routes.</div>
          ) : (
            preset.takes.map((t, i) => (
              <div key={i} className="p-2 text-sm flex items-center gap-2">
                <span className="text-ink truncate flex-1">{sourceName(t.source)}</span>
                <span className="text-sub">→</span>
                <span className="text-ink truncate flex-1 text-right">{destName(t.destination)}</span>
              </div>
            ))
          )}
        </div>

        {status === 'idle' && (
          <div className="flex gap-2 justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-md border border-border bg-panel text-ink hover:border-sub"
            >
              Cancel
            </button>
            <button
              onClick={onFire}
              disabled={total === 0}
              className="px-4 py-2 rounded-md bg-accent text-ink font-semibold disabled:opacity-40"
            >
              Fire
            </button>
          </div>
        )}

        {status === 'firing' && (
          <div className="text-sub text-sm text-center py-2">Firing…</div>
        )}

        {status === 'done' && result && (
          <div className="text-sm">
            {failed.length === 0 ? (
              <div className="text-ok">{successCount}/{total} routes fired</div>
            ) : (
              <div className="text-warn">
                <div>{successCount}/{total} routes fired, {failed.length} failed:</div>
                <ul className="mt-1 list-disc list-inside text-xs text-sub">
                  {failed.slice(0, 5).map((f, i) => (
                    <li key={i}>
                      {sourceName(f.source)} → {destName(f.destination)}: {f.error ?? 'failed'}
                    </li>
                  ))}
                  {failed.length > 5 && <li>…and {failed.length - 5} more</li>}
                </ul>
              </div>
            )}
          </div>
        )}

        {status === 'error' && (
          <div className="text-sm text-err">
            <div>Error: {errorMsg ?? 'failed'}</div>
            <div className="flex gap-2 justify-end mt-2">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-md border border-border bg-panel text-ink hover:border-sub"
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </Sheet>
  )
}
